/**
 * @file     PriceTrend.tsx
 * @purpose  The small price-change line beside a listing's price: a two-point
 *           trend line (red down, green up), "Price down $25,000" and an info
 *           glyph. The whole line links to the Listing history section on the
 *           same surface (smooth scroll, instant under reduced motion). It
 *           reads only this listing's own asking prices (original list price
 *           against the current one), never VOW history. The original list
 *           price is VOW data, so without VOW access the server sends the
 *           direction only and a blurred placeholder button (into the VOW
 *           gate) stands where the amount would be.
 */
'use client';

import { useEffect, useState, type MouseEvent } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import type { ListingSurface } from '@/core/analytics/listingEvents';
import { hubColors } from '@/components/hub/hubKitStyles';
import { ListingIcon } from '@/components/icons/ListingIcons';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { useListingEvents } from '@/hooks/useListingEvents';
import { VowLockedPriceChange } from '@/components/vow/VowLockedPriceChange';

const cad = (n: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

export interface PriceTrendProps {
  direction: 'down' | 'up';
  /** Original list price of this listing; absent without VOW access (direction only). */
  from?: number;
  /** Current list price. */
  to?: number;
  /** Id of the Listing history section to scroll to. */
  targetId: string;
  listingKey: string;
  surface: ListingSurface;
}

export function PriceTrend({ direction, from, to, targetId, listingKey, surface }: PriceTrendProps) {
  const { t } = useTranslation();
  const track = useListingEvents();
  // Server HTML links to the section; a surface without one drops the link once mounted.
  const [hasTarget, setHasTarget] = useState(true);
  useEffect(() => {
    setHasTarget(document.getElementById(targetId) != null);
  }, [targetId]);

  if (from != null && from === to) return null;
  const down = direction === 'down';
  const amount = from != null && to != null ? cad(Math.abs(from - to)) : null;
  const label = amount
    ? down
      ? t('listingDetail.priceTrend.down', { defaultValue: 'Price down {{amount}}', amount })
      : t('listingDetail.priceTrend.up', { defaultValue: 'Price up {{amount}}', amount })
    : down
      ? t('listingDetail.priceTrend.reduced', { defaultValue: 'Price reduced' })
      : t('listingDetail.priceTrend.increased', { defaultValue: 'Price increased' });
  const since = t('listingDetail.priceTrend.since', { defaultValue: 'since listed' });
  const color = down ? hubColors.statusError : hubColors.statusSuccess;

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(targetId);
    if (!target) return;
    event.preventDefault();
    track('price_trend_click', { surface, listing_key: listingKey, direction });
    target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  };

  const content = (
    <>
      <Box component="svg" aria-hidden viewBox="0 0 28 16" width={28} height={16} sx={{ flexShrink: 0, color, overflow: 'visible' }}>
        <polyline
          points={down ? '2,3 11,6 18,8 25,13' : '2,13 11,10 18,8 25,3'}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx={25} cy={down ? 13 : 3} r={2.5} fill="currentColor" />
      </Box>
      <Box component="span" data-price-trend-label sx={{ whiteSpace: 'nowrap' }}>
        {label}
      </Box>
    </>
  );

  const sx = {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'center',
    gap: 1.5,
    minHeight: 32,
    fontSize: 14,
    fontWeight: fontWeightTokens.medium,
    lineHeight: 1.3,
    color: hubColors.textPrimary,
    fontVariantNumeric: 'tabular-nums',
  } as const;

  const trend = !hasTarget ? (
    <Box component="span" data-price-trend={direction} aria-label={`${label} ${since}`} role="img" sx={sx}>
      {content}
    </Box>
  ) : (
    <Box
      component="a"
      href={`#${targetId}`}
      data-price-trend={direction}
      aria-label={`${label} ${since}. ${t('listingDetail.priceTrend.seeHistory', { defaultValue: 'See listing history' })}`}
      onClick={onClick}
      sx={{
        ...sx,
        textDecoration: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        '@media (hover: hover)': { '&:hover [data-price-trend-label]': { textDecoration: 'underline', textUnderlineOffset: '3px' } },
        '&:focus-visible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 2 },
      }}
    >
      {content}
      {/* Locked, the blurred amount follows the label instead. */}
      {amount ? (
        <Box component="span" sx={{ display: 'inline-flex', color: hubColors.textMuted }}>
          <ListingIcon name="info" size={16} />
        </Box>
      ) : null}
    </Box>
  );

  if (amount) return trend;
  // The amount is a sibling, not inside the link: one control per target.
  return (
    <Box component="span" data-price-trend-locked sx={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'center', gap: 0.5, fontSize: 14, fontWeight: fontWeightTokens.medium, color: hubColors.textPrimary }}>
      {trend}
      <VowLockedPriceChange listingKey={listingKey} surface={surface} />
    </Box>
  );
}
